import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableRenderProps,
} from '@carbon/react';
import { format8 } from 'utils/number';
import { SkeletonTableRows } from 'components/skeleton-table-rows/skeleton-table-rows';
import {
  InterestCellValue,
  InterestColumnKey,
  InterestConversionData,
  InterestRowKey,
} from './types';
import { InterestFormulaPopup } from './interest-formula-popup';
import styles from './bundle-of-sticks-table.module.scss';

// Row keys for the Bundle of Sticks table
const ROW_KEYS: InterestRowKey[] = [
  'executive',
  'royalty',
  'bonus',
  'delay',
  'leaseRoyaltyRate',
  'orri',
  'wi',
  'nri',
];

// Display labels for each row
const ROW_LABELS: Record<InterestRowKey, string> = {
  executive: 'Executive',
  royalty: 'Royalty',
  bonus: 'Bonus',
  delay: 'Delay',
  leaseRoyaltyRate: 'Lease Royalty Rate',
  orri: 'ORRI',
  wi: 'WI',
  nri: 'NRI',
};

// Column headers for the Bundle of Sticks table
const COLUMN_HEADERS = [
  { key: 'label', header: '' },
  { key: 'interest', header: 'Interest' },
  { key: 'nma', header: 'NMA' },
  { key: 'nmaPostBurden', header: 'NMA Post Burden' },
  { key: 'nra', header: 'NRA' },
] as const;

/**
 * ============================================================================
 * CELL APPLICABILITY MATRIX
 * ============================================================================
 * Defines which (row, column) combinations should show values vs "--"
 * Based on:
 * - Interest Key Matrix (Screenshot 1)
 * - Product Team Value-to-InstrumentType Mapping (Screenshot 13)
 * - Scenario spreadsheets (Screenshots 2-12)
 *
 * INTEREST COLUMN (from Interest Key Matrix; UMI's WI and NRI as the server's
 * InterestsCalculator::Constants::INTERESTS_BY_INSTRUMENT_TYPE calculates them):
 * ┌─────────────────────┬────┬──────┬─────┬──────┬─────┐
 * │ Row                 │ MD │ NPRI │ OGL │ ORRI │ UMI │
 * ├─────────────────────┼────┼──────┼─────┼──────┼─────┤
 * │ Executive           │ ✓  │  --  │ --  │  --  │  ✓  │
 * │ Royalty             │ ✓  │  ✓   │ --  │  --  │  ✓  │
 * │ Bonus               │ ✓  │  --  │ --  │  --  │  ✓  │
 * │ Delay               │ ✓  │  --  │ --  │  --  │  ✓  │
 * │ Lease Royalty Rate  │ ✓  │  ✓   │  ✓  │  ✓   │  ✓  │
 * │ ORRI                │ -- │  --  │ --  │  ✓   │ --  │
 * │ WI                  │ -- │  --  │  ✓  │  --  │  ✓  │
 * │ NRI                 │ ✓  │  ✓   │  ✓  │  ✓   │  ✓  │
 * └─────────────────────┴────┴──────┴─────┴──────┴─────┘
 *
 * NMA COLUMN (from Screenshot 13: NMA applicable to MD, UMI, OGL):
 * - MD:   Executive row (NMA = Executive × GrossAcres)
 * - NPRI: Not applicable
 * - OGL:  WI row (NMA = WI × GrossAcres)
 * - ORRI: Not applicable
 * - UMI:  Executive row (NMA = Executive × GrossAcres)
 *
 * NMA POST BURDEN COLUMN (from Screenshot 13: applicable to all):
 * - MD:   Royalty row (Royalty × GrossAcres)
 * - NPRI: Royalty row (Royalty × GrossAcres)
 * - OGL:  NRI row ((WI_NRI / (1 - LeaseRoyaltyRate)) × GrossAcres)
 * - ORRI: ORRI row (ORRI × GrossAcres)
 * - UMI:  Royalty row (Royalty × GrossAcres)
 *
 * NRA COLUMN (from Screenshot 13: NRA applicable to MD, NPRI, ORRI):
 * - MD:   NRI row (GrossAcres × NRI × 8)
 * - NPRI: NRI row (GrossAcres × NRI × 8)
 * - OGL:  Not applicable
 * - ORRI: NRI row (GrossAcres × NRI × 8)
 * - UMI:  Not applicable
 * ============================================================================
 */

type InstrumentType = 'MD' | 'NPRI' | 'OGL' | 'ORRI' | 'UMI';

/**
 * Interest Column: Which rows show values for each instrument type.
 * Rows not in this set will show '--' instead of hiding.
 */
const INTEREST_ROWS: Record<InstrumentType, Set<InterestRowKey>> = {
  MD: new Set(['executive', 'royalty', 'bonus', 'delay', 'nri']),
  NPRI: new Set(['royalty', 'nri']),
  OGL: new Set(['wi', 'nri']),
  ORRI: new Set(['orri', 'nri']),
  // Unleased: the calculator gives WI = NRI = Royalty (no lease burden).
  UMI: new Set(['executive', 'royalty', 'bonus', 'delay', 'wi', 'nri']),
};

/**
 * NMA Column: Which row shows the value (null = not applicable for this instrument type)
 */
const NMA_ROW: Record<InstrumentType, InterestRowKey | null> = {
  MD: 'executive',
  NPRI: null,
  OGL: 'wi',
  ORRI: null,
  UMI: 'executive',
};

/**
 * NMA Post Burden Column: Which row shows the value
 */
const POST_BURDEN_NMA_ROW: Record<InstrumentType, InterestRowKey> = {
  MD: 'royalty',
  NPRI: 'royalty',
  OGL: 'nri',
  ORRI: 'orri',
  UMI: 'royalty',
};

/**
 * NRA Column: Which row shows the value (null = not applicable for this instrument type)
 */
const NRA_ROW: Record<InstrumentType, InterestRowKey | null> = {
  MD: 'nri',
  NPRI: 'nri',
  OGL: null,
  ORRI: 'nri',
  UMI: null, // Unleased - no NRA
};

/**
 * Whether the table has rows for the interest type, matched exactly.
 * @param {string} type - The interest type's value
 * @return {boolean} True for a type the table knows
 */
const isInstrumentType = (type: string): type is InstrumentType =>
  Object.prototype.hasOwnProperty.call(INTEREST_ROWS, type);

/**
 * Check if a cell should display a value or "--"
 * @param {InterestRowKey} rowKey - The row identifier
 * @param {InterestColumnKey} columnKey - The column identifier
 * @param {string | null} instrumentType - The instrument type
 * @return {boolean} True if the cell should display a value
 */
const isCellApplicable = (
  rowKey: InterestRowKey,
  columnKey: InterestColumnKey,
  instrumentType: string | null
): boolean => {
  // Lease Royalty Rate only shows in Interest column (it's an input, not calculated)
  if (rowKey === 'leaseRoyaltyRate') {
    return columnKey === 'interest';
  }

  // The type matched exactly, as the server matches it; no type, or one the
  // table does not know (a stored legacy value such as "Mineral Deed"), shows
  // MD's rows.
  const effectiveType: InstrumentType =
    instrumentType && isInstrumentType(instrumentType) ? instrumentType : 'MD';

  switch (columnKey) {
    case 'interest':
      return INTEREST_ROWS[effectiveType].has(rowKey);
    case 'nma':
      return NMA_ROW[effectiveType] === rowKey;
    case 'nmaPostBurden':
      return POST_BURDEN_NMA_ROW[effectiveType] === rowKey;
    case 'nra':
      return NRA_ROW[effectiveType] === rowKey;
    default:
      return false;
  }
};

interface BundleOfSticksTableProps {
  data: InterestConversionData;
  loading?: boolean;
  instrumentType?: string | null;
}

const renderCell = (
  cellValue: InterestCellValue,
  rowKey: InterestRowKey,
  columnKey: InterestColumnKey,
  instrumentType: string | null
): React.ReactNode => {
  const isApplicable = isCellApplicable(rowKey, columnKey, instrumentType);

  // If cell is not applicable for this row/column, show '--'
  if (!isApplicable) {
    return (
      <div className={styles.cellContent}>
        <span className={styles.emptyText}>--</span>
      </div>
    );
  }

  const formattedValue = format8(cellValue.value);
  // Show formula icon if value exists and has formula
  const showFormula =
    formattedValue !== '--' && cellValue.showCalculation && cellValue.formula;

  return (
    <div className={styles.cellContent}>
      <span
        className={
          formattedValue !== '--' ? styles.valueText : styles.emptyText
        }
      >
        {formattedValue}
      </span>
      {showFormula && (
        <InterestFormulaPopup
          rowKey={rowKey}
          columnKey={columnKey}
          formula={cellValue.formula!}
        />
      )}
    </div>
  );
};

export const BundleOfSticksTable = ({
  data,
  loading = false,
  instrumentType = null,
}: BundleOfSticksTableProps) => {
  // All rows are always visible - non-applicable cells show '--'
  const tableRows = ROW_KEYS.map((key, index) => {
    const rowData = data[key];
    return {
      id: String(index),
      label: ROW_LABELS[key],
      rowKey: key,
      interest: rowData.interest,
      nma: rowData.nma,
      nmaPostBurden: rowData.nmaPostBurden,
      nra: rowData.nra,
    };
  });

  if (loading) {
    return (
      <div className={styles.tableWrapper}>
        <Table className={styles.table}>
          <colgroup>
            <col className={styles.labelCol} />
            <col className={styles.valueCol} />
            <col className={styles.valueCol} />
            <col className={styles.valueCol} />
            <col className={styles.valueCol} />
          </colgroup>
          <TableHead>
            <TableRow>
              {COLUMN_HEADERS.map((header) => (
                <TableHeader
                  key={header.key}
                  className={
                    header.key === 'label'
                      ? styles.labelHeader
                      : styles.valueHeader
                  }
                >
                  {header.header}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <SkeletonTableRows
              rows={ROW_KEYS.length}
              columns={COLUMN_HEADERS.length}
            />
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <DataTable rows={tableRows} headers={[...COLUMN_HEADERS]}>
        {({
          rows,
          headers,
          getTableProps,
          getHeaderProps,
          getRowProps,
        }: DataTableRenderProps<any, any[]>) => (
          <Table {...getTableProps()} className={styles.table}>
            <colgroup>
              <col className={styles.labelCol} />
              <col className={styles.valueCol} />
              <col className={styles.valueCol} />
              <col className={styles.valueCol} />
              <col className={styles.valueCol} />
            </colgroup>
            <TableHead>
              <TableRow>
                {headers.map((header: any) => (
                  <TableHeader
                    {...getHeaderProps({ header })}
                    key={header.key}
                    className={
                      header.key === 'label'
                        ? styles.labelHeader
                        : styles.valueHeader
                    }
                  >
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: any, rowIndex: number) => {
                const rowKey = ROW_KEYS[rowIndex];
                return (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map((cell: any) => {
                      const columnKey = cell.info.header as string;

                      if (columnKey === 'label') {
                        return (
                          <TableCell key={cell.id} className={styles.labelCell}>
                            {cell.value}
                          </TableCell>
                        );
                      }

                      const cellValue = cell.value as InterestCellValue;
                      return (
                        <TableCell key={cell.id} className={styles.valueCell}>
                          {renderCell(
                            cellValue,
                            rowKey,
                            columnKey as InterestColumnKey,
                            instrumentType
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DataTable>
    </div>
  );
};
